import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { BaseLayout } from './BaseLayout';
import { devices } from '../../../util/cssMediaQuery.util';
import background from '../../../assets/img/gogurt/splash-bg.jpg';
import { motion, useAnimationControls } from 'framer-motion';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { BrandStore } from '../../../context';

interface LandingLayoutProps {
  children: React.ReactNode;
  triggerAnimateSplash?: boolean;
  reversed?: boolean;
}

const TwoColWrap = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  margin: 0;
  height: 100vh;
  ${devices.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const SplashWrap = styled(motion.div)`
  display: none;

  ${devices.tablet} {
    display: block;
    position: relative;
    z-index: 2;
    order: 1;
    order: ${(props: LandingLayoutProps) => (props.reversed ? 1 : 2)};
  }
`;

const SplashBg = styled.div`
  display: none;

  ${devices.tablet} {
    display: block;
    width: auto;
    height: 100%;
    ${(props: { backgroundImage?: string }) => `
    background: url(${
      props?.backgroundImage || background
    }) bottom right no-repeat;`}
    background-size: cover;
  }
`;

const Main = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  z-index: 1;

  ${devices.tablet} {
    order: ${(props: LandingLayoutProps) => (props.reversed ? 2 : 1)};
  }
`;

export const LandingLayout: React.FunctionComponent<
  LandingLayoutProps
> = props => {
  const { children, triggerAnimateSplash, reversed } = props;
  const { isMobile } = useWindowSize();
  const controls = useAnimationControls();
  const [hasAnimated, setHasAnimated] = useState(false);
  const {
    state: { brand, brandName },
  } = useContext(BrandStore);
  const [backgroundImage] =
    brandName !== 'fattmerchant' ? brand?.signUpFlowWelcomeImage || [] : [];

  useEffect(() => {
    // This animation shouldn't happen on mobile, according to the design.
    if (!isMobile && triggerAnimateSplash && !hasAnimated) {
      controls.start({
        x: ['100%', '0%'],
        transition: { delay: 0.2, ease: [0.87, 0, 0.13, 1], duration: 1 },
      });
      setHasAnimated(true);
    }
  }, [triggerAnimateSplash, controls, hasAnimated, isMobile]);

  return (
    <BaseLayout>
      <TwoColWrap>
        <Main reversed={reversed}>
          {children}
        </Main>
        <SplashWrap key="splash" animate={controls} reversed={reversed}>
          <SplashBg backgroundImage={backgroundImage?.url} />
        </SplashWrap>
      </TwoColWrap>
    </BaseLayout>
  );
};
